"use client";

import React from "react";

// ── Stat highlight card ──────────────────────────────────────────────
interface StatCardProps {
  value: string;
  label: string;
  source?: string;
  sourceUrl?: string;
  color?: "blue" | "green" | "orange" | "red" | "purple" | "violet" | "fuchsia";
}

const colorMap = {
  blue: "from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-400",
  green: "from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 text-emerald-400",
  orange: "from-yellow-500/20 to-yellow-600/5 border-yellow-500/30 text-yellow-400",
  red: "from-red-500/20 to-red-600/5 border-red-500/30 text-red-400",
  purple: "from-purple-500/20 to-purple-600/5 border-purple-500/30 text-purple-400",
  violet: "from-violet-500/20 to-violet-600/5 border-violet-500/30 text-violet-400",
  fuchsia: "from-fuchsia-500/20 to-fuchsia-600/5 border-fuchsia-500/30 text-fuchsia-400",
};

export function StatCard({ value, label, source, sourceUrl, color = "blue" }: StatCardProps) {
  const c = colorMap[color] ?? colorMap.purple;
  return (
    <div className={`my-6 rounded-xl border bg-gradient-to-br p-6 text-center ${c}`}>
      <div className="text-4xl font-extrabold tracking-tight md:text-5xl">{value}</div>
      <div className="mt-2 text-sm font-medium text-muted-foreground">{label}</div>
      {source && (
        <div className="mt-3 text-xs text-muted-foreground/60">
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
              Source : {source}
            </a>
          ) : (
            <>Source : {source}</>
          )}
        </div>
      )}
    </div>
  );
}

// ── Stats grid (2-3 stat cards side by side) ─────────────────────────
interface StatsGridProps {
  children: React.ReactNode;
}

export function StatsGrid({ children }: StatsGridProps) {
  return (
    <div className="my-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  );
}

export function StatsGridCard({ value, label, source, sourceUrl, color = "blue" }: StatCardProps) {
  const c = colorMap[color] ?? colorMap.purple;
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-5 text-center ${c}`}>
      <div className="text-3xl font-extrabold tracking-tight">{value}</div>
      <div className="mt-1.5 text-xs font-medium text-muted-foreground">{label}</div>
      {source && (
        <div className="mt-2 text-[10px] text-muted-foreground/60">
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
              {source}
            </a>
          ) : (
            source
          )}
        </div>
      )}
    </div>
  );
}
StatsGrid.Card = StatsGridCard;

// ── Horizontal bar chart ─────────────────────────────────────────────
interface BarChartItem {
  label: string;
  value: number;
  displayValue?: string;
}

interface BarChartProps {
  title?: string;
  items: BarChartItem[];
  maxValue?: number;
  color?: "blue" | "green" | "orange" | "purple";
  source?: string;
  sourceUrl?: string;
}

const barColors = {
  blue: "bg-blue-500",
  green: "bg-emerald-500",
  orange: "bg-yellow-500",
  purple: "bg-purple-500",
  violet: "bg-violet-500",
  fuchsia: "bg-fuchsia-500",
};

export function BarChart({ title, items, maxValue, color = "blue", source, sourceUrl }: BarChartProps) {
  const max = maxValue || Math.max(...items.map((i) => i.value));
  return (
    <div className="my-8 rounded-xl border bg-muted/20 p-6">
      {title && <div className="mb-4 text-sm font-semibold text-foreground">{title}</div>}
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i}>
            <div className="mb-1 flex items-center justify-between text-xs">
              <span className="font-medium text-muted-foreground">{item.label}</span>
              <span className="font-bold text-foreground">{item.displayValue ?? `${item.value}%`}</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-muted/50">
              <div
                className={`h-full rounded-full ${barColors[color] ?? barColors.purple} transition-all duration-700`}
                style={{ width: `${Math.min((item.value / max) * 100, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      {source && (
        <div className="mt-4 text-[10px] text-muted-foreground/60">
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
              Source : {source}
            </a>
          ) : (
            <>Source : {source}</>
          )}
        </div>
      )}
    </div>
  );
}

// ── Flow / step diagram (horizontal on desktop, vertical on mobile) ──
interface FlowStep {
  label: string;
  description?: string;
  accent?: string;
}

interface FlowDiagramProps {
  steps: FlowStep[];
  title?: string;
}

export function FlowDiagram({ steps, title }: FlowDiagramProps) {
  return (
    <div className="my-8 rounded-xl border bg-muted/20 p-6">
      {title && <div className="mb-5 text-center text-sm font-semibold text-foreground">{title}</div>}
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-start md:gap-0">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <div className="flex flex-1 flex-col items-center rounded-lg bg-background/60 p-4 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                {step.accent || i + 1}
              </div>
              <div className="mt-2 text-sm font-semibold text-foreground">{step.label}</div>
              {step.description && (
                <div className="mt-1 text-xs text-muted-foreground">{step.description}</div>
              )}
            </div>
            {i < steps.length - 1 && (
              <>
                <div className="hidden md:flex items-center px-1 pt-6 text-muted-foreground/40">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
                <div className="flex md:hidden items-center justify-center py-0.5 text-muted-foreground/40">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                </div>
              </>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Callout / tip box ────────────────────────────────────────────────
interface CalloutProps {
  type?: "tip" | "warning" | "info" | "stat";
  title?: string;
  children: React.ReactNode;
}

const calloutStyles = {
  tip: "border-emerald-500/30 bg-emerald-500/5",
  warning: "border-yellow-500/30 bg-yellow-500/5",
  info: "border-blue-500/30 bg-blue-500/5",
  stat: "border-purple-500/30 bg-purple-500/5",
};

const calloutIcons = {
  tip: "💡",
  warning: "⚠️",
  info: "ℹ️",
  stat: "📊",
};

export function Callout({ type = "tip", title, children }: CalloutProps) {
  return (
    <div className={`my-6 rounded-xl border p-5 ${calloutStyles[type]}`}>
      <div className="flex items-start gap-3">
        <span className="text-lg">{calloutIcons[type]}</span>
        <div className="flex-1">
          {title && <div className="mb-1 text-sm font-semibold text-foreground">{title}</div>}
          <div className="text-sm text-muted-foreground leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

// ── Comparison (side by side) ────────────────────────────────────────
interface ComparisonProps {
  leftTitle: string;
  rightTitle: string;
  leftItems: string[];
  rightItems: string[];
  leftColor?: "green" | "red" | "blue" | "purple" | "violet" | "fuchsia";
  rightColor?: "green" | "red" | "blue" | "purple" | "violet" | "fuchsia";
}

const compColors = {
  green: "border-emerald-500/30 bg-emerald-500/5",
  red: "border-red-500/30 bg-red-500/5",
  blue: "border-blue-500/30 bg-blue-500/5",
  purple: "border-purple-500/30 bg-purple-500/5",
  violet: "border-violet-500/30 bg-violet-500/5",
  fuchsia: "border-fuchsia-500/30 bg-fuchsia-500/5",
};

const compHeaderColors = {
  green: "text-emerald-400",
  red: "text-red-400",
  blue: "text-blue-400",
  purple: "text-purple-400",
  violet: "text-violet-400",
  fuchsia: "text-fuchsia-400",
};

export function Comparison({
  leftTitle,
  rightTitle,
  leftItems,
  rightItems,
  leftColor = "green",
  rightColor = "red",
}: ComparisonProps) {
  return (
    <div className="my-8 grid gap-4 sm:grid-cols-2">
      <div className={`rounded-xl border p-5 ${compColors[leftColor] ?? compColors.purple}`}>
        <div className={`mb-3 text-sm font-bold ${compHeaderColors[leftColor] ?? compHeaderColors.purple}`}>{leftTitle}</div>
        <ul className="space-y-2">
          {leftItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-0.5 text-emerald-400">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div className={`rounded-xl border p-5 ${compColors[rightColor] ?? compColors.violet}`}>
        <div className={`mb-3 text-sm font-bold ${compHeaderColors[rightColor] ?? compHeaderColors.violet}`}>{rightTitle}</div>
        <ul className="space-y-2">
          {rightItems.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="mt-0.5 text-red-400">✗</span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Donut / pie chart (single value) ─────────────────────────────────
interface DonutProps {
  value: number;
  label: string;
  sublabel?: string;
  color?: "blue" | "green" | "orange" | "purple";
}

const donutColors = {
  blue: "stroke-blue-500",
  green: "stroke-emerald-500",
  orange: "stroke-yellow-500",
  purple: "stroke-purple-500",
};

export function Donut({ value, label, sublabel, color = "blue" }: DonutProps) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <svg width="100" height="100" viewBox="0 0 100 100" className="-rotate-90">
        <circle cx="50" cy="50" r={radius} fill="none" stroke="currentColor" className="text-muted/30" strokeWidth="8" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          className={donutColors[color]}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="mt-2 text-center">
        <div className="text-lg font-bold text-foreground">{value}%</div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {sublabel && <div className="text-[10px] text-muted-foreground/60">{sublabel}</div>}
      </div>
    </div>
  );
}

// ── Donut grid (multiple donuts side by side) ────────────────────────
interface DonutGridProps {
  children: React.ReactNode;
  title?: string;
  source?: string;
  sourceUrl?: string;
}

export function DonutGrid({ children, title, source, sourceUrl }: DonutGridProps) {
  return (
    <div className="my-8 rounded-xl border bg-muted/20 p-6">
      {title && <div className="mb-5 text-center text-sm font-semibold text-foreground">{title}</div>}
      <div className="flex flex-wrap items-start justify-center gap-8">
        {children}
      </div>
      {source && (
        <div className="mt-4 text-center text-[10px] text-muted-foreground/60">
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">
              Source : {source}
            </a>
          ) : (
            <>Source : {source}</>
          )}
        </div>
      )}
    </div>
  );
}
